"use client";

import { useState } from "react";
import {
  calculateCharDasha,
  type CharDashaRequest,
  type CharDashaResponse,
} from "@/lib/api/char-dasha";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import InlineAppCTA from "@/components/InlineAppCTA";
import AIAstrologerCTA from "@/components/AIAstrologerCTA";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import { CharDashaResult } from "./_results";
import { CharDashaSeoContent } from "./_seo-content";

export default function CharDashaClient() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [result, setResult] = useState<CharDashaResponse | null>(null);

  async function handleSubmit(data: Parameters<typeof calculateCharDasha>[0]) {
    setLoading(true);
    setError(undefined);

    try {
      const response = await calculateCharDasha(data as CharDashaRequest);
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    return <CharDashaResult result={result} />;
  }

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      {/* Hero */}
      <div className="mx-auto max-w-lg px-4 sm:px-6 pt-12 sm:pt-20 pb-6">
        <div className="text-center">
          <p
            className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-4"
            style={{ color: "var(--ish-accent)" }}
          >
            Jaimini Astrology
          </p>
          <h1
            className="text-[32px] sm:text-[42px] leading-[1.15] font-bold"
            style={{
              fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
              color: "var(--ish-dark)",
            }}
          >
            Free Char Dasha <span style={{ color: "var(--ish-accent)" }}>Calculator</span>
          </h1>
          <p
            className="mt-4 text-[15px] leading-relaxed max-w-md mx-auto"
            style={{ color: "var(--ish-text-muted)" }}
          >
            Calculate your Jaimini Rashi Dasha sequence. Find your Atmakaraka, Amatyakaraka, and all
            12 Char Dasha periods from your birth chart.
          </p>

          {/* Trust signals */}
          <div
            className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] font-medium"
            style={{ color: "var(--ish-text-faint)" }}
          >
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Jaimini Sutras
            </span>
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              Swiss Ephemeris
            </span>
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M9 21V9" />
              </svg>
              12 Rashi Dasha Periods
            </span>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="mx-auto max-w-md px-4 sm:px-6 pb-8">
        <BirthDetailsForm
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          submitLabel="Calculate Char Dasha"
          analyticsToolName="char-dasha"
        />
      </div>

      {/* SEO Content */}
      <div className="mx-auto max-w-2xl px-4 sm:px-6 pb-4">
        <InlineAppCTA />
        <div className="divider-warm mb-10" />
      </div>

      <CharDashaSeoContent />

      <div className="mx-auto max-w-2xl px-4 sm:px-6 pb-12 sm:pb-16">
        <AIAstrologerCTA context="kundli" />
        <CalculatorCrossLinks currentPage="char-dasha" />
      </div>
    </div>
  );
}
